import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Chip,
  IconButton,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { ArrowIcon } from "../../components/icon-components/ArrowIcon";
import styles from "./VideoReaderCard.module.scss";
import CompleteIllustratorIcon from "../../components/icon-components/CompleteIllustratorIcon";
import { useTranslation } from "react-i18next";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import PlayIcon from "../../components/icon-components/PlayIcon";
import PlayButtonIcon from "../../components/icon-components/PlayButtonIcon";
import CheckedCircleIcon from "../../components/icon-components/CheckedCircleIcon";

interface VideoReaderCardProps {
  category?: string;
  isNew?: boolean;
  activityPoints?: number;
  title?: string;
  videoUrl?: string;
  isCompleteBtnVisible?: boolean;
  thumbnail?: string;
  summarry?: string;
  length?: number;
  publishedDate?: string;
  publisherName?: string;
  hideCheckMark?: boolean;
  onComplete?: () => void;
  navigateBack: () => void;
}

const VideoReaderCard: React.FC<VideoReaderCardProps> = ({
  category = "Nutrition",
  isNew,
  activityPoints,
  title,
  videoUrl,
  isCompleteBtnVisible,
  thumbnail,
  publishedDate,
  publisherName,
  summarry,
  hideCheckMark,
  onComplete,
  navigateBack,
  length,
}) => {
  const { t } = useTranslation();
  const [completedVideo, setCompletedVideo] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto"});
  }, []);

  const handleVideoEnd = () => {
    setCompletedVideo(true);
  };

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.headingContainer}>
        <Box className={styles.leftHeader}>
          <IconButton sx={{ paddingLeft: 0 }} onClick={navigateBack}>
            <ArrowIcon width={20} height={15} />
          </IconButton>
          <Typography variant="bodyOverline" className={styles.categoryText}>
            {category}
          </Typography>
        </Box>
        <Box className={styles.rightHeader}>
          {!isCompleteBtnVisible && !hideCheckMark && (
            <CheckedCircleIcon width={20} height={20} variant={"completed"} />
          )}
          {isNew && (
            <Chip
              label={t("educationContent.new")}
              className={styles.newChip}
            />
          )}
          {activityPoints! > 0 && (
            <Chip
              label={t("viewLearningContent.points", {
                points: activityPoints,
              })}
              className={styles.pointsChip}
            />
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box className={styles.contentContainer}>
        <Typography variant="h2">{title}</Typography>
        {publisherName && (
          <Box>
            <Typography variant="body2">{publisherName}</Typography>
            <Typography variant="body2" sx={{ color: "#666666" }}>
              {publishedDate}
            </Typography>
          </Box>
        )}
        {isCompleteBtnVisible && completedVideo ? (
          // Show completion UI instead of content
          <Card className={styles.completeCard}>
            <CardContent>
              <Box className={styles.completeContainer}>
                <CompleteIllustratorIcon height={85} width={120} />
                <Box className={styles.confirmMessage}>
                  <Typography variant="h5" textAlign={"center"}>
                    {t("viewLearningContent.readVideoMessage")}
                  </Typography>
                  <Typography variant="body1" textAlign={"center"}>
                    {t("viewLearningContent.completeConfirmationMessage", {
                      activityPoints: activityPoints,
                    })}
                  </Typography>
                  <CustomButton
                    variant="contained"
                    color="primary"
                    text={t("completedBtn")}
                    sx={{ width: "134px" }}
                    onClick={onComplete}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            <Box className={styles.videoContainer}>
              {!hasStarted ? (
                <Box
                  className={styles.thumbnailWrapper}
                  onClick={() => setHasStarted(true)}
                >
                  <img
                    src={thumbnail}
                    alt="Video thumbnail"
                    className={styles.thumbnailImage}
                  />
                  <Box className={styles.playButtonOverlay}>
                    <PlayIcon height={72} width={72} />
                  </Box>
                </Box>
              ) : (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  className={styles.videoPlayer}
                  data-testid="video-player"
                  controls
                  autoPlay
                  muted={false}
                  onEnded={handleVideoEnd}
                  controlsList="nodownload noplaybackrate"
                />
              )}
              {length != null && length !== 0 && (
                <Box display="flex" gap={2} alignItems="center">
                  <PlayButtonIcon height={24} width={24} fill="#4E7E6E" />
                  <Typography variant="body1">
                    {length < 1
                      ? t("educationContent.lessThanAMin")
                      : t("viewLearningContent.mediaDurationInminutes", {
                          minutes: length,
                        })}
                  </Typography>
                </Box>
              )}
              <Typography variant="body1">{summarry}</Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default VideoReaderCard;
